<script lang="ts" setup>
import { ref, watch, onMounted, nextTick, toRefs, computed, inject } from 'vue';
import { isMobile, isTablet } from 'mobile-device-detect';
import { Settings } from '../constant';
import PlusCircleIcon from '@/assets/icons/PlusCircle.vue';
import MinusCircleIcon from '@/assets/icons/MinusCircle.vue';
import PrinterIcon from '@/assets/icons/Printer.vue';

interface Props {
  zoomInDisabled?: boolean;
  zoomOutDisabled?: boolean;
  headerOpen: boolean;
  isKioskMode: boolean;
  toolbar: boolean;
}

const props = defineProps<Props>();

const {
  zoomInDisabled,
  zoomOutDisabled,
  headerOpen,
  isKioskMode,
  toolbar,
} = toRefs(props);

const emit = defineEmits([
  'zoomIn',
  'zoomOut',
  'handlePrintClick',
  'calculateContactInfoTopPosition',
  'autoFit',
  'toggleToolbar',
]);

// injector
const showDivisionCommunityDropdown = inject('setSelectionDropdown', ref(false));

const toolbarTopPosition = ref(0);
const autoZoom = ref(false);

const zoomIn = () => emit('zoomIn');
const zoomOut = () => emit('zoomOut');

const handlePrintClick = () => {
  calculateContactInfoTopPosition();
  emit('handlePrintClick');
};

const calculateContactInfoTopPosition = () =>
  emit('calculateContactInfoTopPosition');

const autoFit = () => {
  autoZoom.value = !autoZoom.value;
  emit('autoFit');
};

const toggleToolbar = () => emit('toggleToolbar');

const calculateToolbarTopPosition = () => {
  const outerHeight =
    isMobile && !isTablet
      ? Settings.TOP_OUTER_HEIGHT_MOBILE
      : Settings.TOP_OUTER_HEIGHT_TABLET;
  const header = document.getElementById('header') as HTMLElement;
  toolbarTopPosition.value =
    (header && header.clientHeight
      ? header.clientHeight
      : Settings.TOP_OUTER_HEIGHT_TABLET) + outerHeight;
};

watch(
  zoomInDisabled,
  (disabled) => {
    nextTick(() => {
      const zoomInEl = document.querySelector('.zoom_in') as HTMLDivElement;
      if (zoomInEl) {
        zoomInEl.style.pointerEvents = disabled ? 'none' : 'auto';
        zoomInEl.style.opacity = disabled ? '0.5' : '1';
      }
    });
  },
  { immediate: true }
);

watch(
  zoomOutDisabled,
  (disabled) => {
    nextTick(() => {
      const zoomOutEl = document.querySelector('.zoom_out') as HTMLDivElement;
      if (zoomOutEl) {
        zoomOutEl.style.pointerEvents = disabled ? 'none' : 'auto';
        zoomOutEl.style.opacity = disabled ? '0.5' : '1';
      }
    });
  },
  { immediate: true }
);

watch(headerOpen, (newVal, oldVal) => {
  if (newVal !== oldVal) calculateToolbarTopPosition();
});

onMounted(() => {
  calculateToolbarTopPosition();
});
defineExpose({
  calculateToolbarTopPosition,
});

const isHorizontal = computed(() => {
  
  return showDivisionCommunityDropdown.value;
});
</script>

<template>
  <div :style="{ top: `${toolbarTopPosition}px` }" class="embtoolbox">
    <div @click="toggleToolbar" v-if="!toolbar">
      <span class="icon icon-header-collapsed">
        <span class="path1"></span>
        <span class="path2"></span>
      </span>
    </div>
    <div class="emb-card" :class="isHorizontal ? 'flexRow' : 'flexCol'" v-else>
      <div
        v-if="!isKioskMode && !isHorizontal"
        class="dsize tbIcon"
        @click="handlePrintClick"
      >
        <PrinterIcon />
      </div>
      <div class="dsize tbIcon zoom_in" @click="zoomIn">
        <PlusCircleIcon />
      </div>
      <div class="dsize tbIcon zoom_out" @click="zoomOut">
        <MinusCircleIcon />
      </div>
      <div v-if="isHorizontal">|</div>
      <div
        v-if="!isKioskMode && isHorizontal"
        class="dsize tbIcon"
        @click="handlePrintClick"
      >
        <PrinterIcon />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.embtoolbox {
  right: 20px;
  top: calc(var(--cpFilterHeight) + 35px) !important;

  @include md {
    top: 93px !important;
    &:has(.flexRow){
      width:165px;
    .emb-card{
      justify-content: space-between;
    }
    }
  }
}
</style>
